/**
 * Tiles of the digital resources on the portal: one click opens the service
 * in a new tab with the patron already signed in.
 */

import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  BookOpen,
  Clapperboard,
  ExternalLink,
  GraduationCap,
  Library,
  Loader2,
  Lock,
  MonitorPlay,
  Newspaper,
  type LucideIcon,
} from "lucide-react";
import { useOpenDigitalResource, type PortalDigitalResource } from "./useDigitalResources";

const PROVIDER_ICONS: Record<string, LucideIcon> = {
  cafeyn: Newspaper,
  mozaik: GraduationCap,
  maxicours: GraduationCap,
  bibliovox: BookOpen,
  cvs: MonitorPlay,
  arte_vod: Clapperboard,
};

interface DigitalResourceTilesProps {
  resources: PortalDigitalResource[];
}

export function DigitalResourceTiles({ resources }: DigitalResourceTilesProps) {
  const { open, pendingId, error } = useOpenDigitalResource();

  return (
    <>
      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {resources.map((resource) => (
          <li key={resource.id}>
            <Tile
              resource={resource}
              pending={pendingId === resource.id}
              error={error?.id === resource.id ? error.message : null}
              onOpen={() => void open(resource)}
            />
          </li>
        ))}
      </ul>
    </>
  );
}

interface TileProps {
  resource: PortalDigitalResource;
  pending: boolean;
  error: string | null;
  onOpen: () => void;
}

const tileClass =
  "group flex h-full w-full items-start gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left dark:border-gray-700 dark:bg-gray-800";
const interactiveClass =
  "transition-shadow hover:border-blue-300 hover:shadow-md focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none motion-reduce:transition-none dark:hover:border-blue-700 dark:focus:ring-offset-gray-900";

function Tile({ resource, pending, error, onOpen }: TileProps) {
  const location = useLocation();
  const body = <TileBody resource={resource} pending={pending} />;

  if (resource.access === "available") {
    return (
      <div className="h-full">
        <button
          type="button"
          onClick={onOpen}
          disabled={pending}
          aria-describedby={error ? `digital-error-${resource.id}` : undefined}
          className={`${tileClass} ${interactiveClass} disabled:cursor-wait`}
        >
          {body}
        </button>
        {error && (
          <p
            id={`digital-error-${resource.id}`}
            role="alert"
            className="mt-2 text-sm text-red-700 dark:text-red-400"
          >
            {error}
          </p>
        )}
      </div>
    );
  }

  if (resource.access === "login_required") {
    return (
      <Link
        to="/opac/login"
        state={{ from: location.pathname }}
        className={`${tileClass} ${interactiveClass}`}
      >
        {body}
      </Link>
    );
  }

  // Not openable by this patron: informative, not a control
  return (
    <div className={`${tileClass} opacity-80`} aria-disabled="true">
      {body}
    </div>
  );
}

function TileBody({ resource, pending }: { resource: PortalDigitalResource; pending: boolean }) {
  return (
    <>
      <Logo resource={resource} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5 font-semibold text-gray-900 dark:text-white">
          {resource.name}
          {resource.access === "available" && (
            <ExternalLink
              className="h-3.5 w-3.5 shrink-0 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400"
              aria-hidden
            />
          )}
        </span>
        <span className="block text-xs font-medium tracking-wide text-blue-700 uppercase dark:text-blue-400">
          {resource.category}
        </span>
        {resource.description && (
          <span className="mt-1 line-clamp-2 block text-sm text-gray-600 dark:text-gray-300">
            {resource.description}
          </span>
        )}
        <AccessNote access={resource.access} pending={pending} />
      </span>
    </>
  );
}

function AccessNote({
  access,
  pending,
}: {
  access: PortalDigitalResource["access"];
  pending: boolean;
}) {
  const noteClass = "mt-2 flex items-center gap-1.5 text-sm";

  switch (access) {
    case "available":
      return (
        <span className={`${noteClass} font-medium text-blue-700 dark:text-blue-400`}>
          {pending ? (
            <>
              <Loader2
                className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
                aria-hidden
              />
              Connexion…
            </>
          ) : (
            <>
              Accéder
              <span className="sr-only"> (s&apos;ouvre dans un nouvel onglet)</span>
            </>
          )}
        </span>
      );
    case "login_required":
      return (
        <span className={`${noteClass} font-medium text-blue-700 dark:text-blue-400`}>
          <Lock className="h-3.5 w-3.5" aria-hidden />
          Se connecter pour accéder
        </span>
      );
    case "not_allowed":
      return (
        <span className={`${noteClass} text-gray-500 dark:text-gray-400`}>
          Non inclus dans votre abonnement
        </span>
      );
    case "account_inactive":
      return (
        <span className={`${noteClass} text-amber-700 dark:text-amber-400`}>
          Abonnement inactif : contactez la bibliothèque
        </span>
      );
  }
}

function Logo({ resource }: { resource: PortalDigitalResource }) {
  const [failed, setFailed] = useState(false);
  const Icon = PROVIDER_ICONS[resource.provider] ?? Library;

  return (
    <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-100 bg-white dark:border-gray-700">
      {resource.logoUrl && !failed ? (
        <img
          src={resource.logoUrl}
          alt=""
          width={56}
          height={56}
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => {
            setFailed(true);
          }}
          className="h-full w-full object-contain p-1.5"
        />
      ) : (
        <Icon className="h-7 w-7 text-blue-600" aria-hidden />
      )}
    </span>
  );
}
